import React, { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Globe, Mail, Linkedin, Phone, Plus, X, Sparkles, Loader2, ChevronDown, ChevronRight, Download, Upload, CheckCircle2, ShoppingCart, AlertCircle } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'
import { supabase } from '@/integrations/supabase/client'
import type { ClientInfrastructureForm, Client } from '@/types/client'
import { DomainPurchaseModal } from '@/components/domains/DomainPurchaseModal'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import * as XLSX from 'xlsx'
import { 
  generateDomainsForCount,
  generateDomainVariations,
  extractRootDomain, 
  getTierLabel,
  getTierDescription,
  type GeneratedDomain 
} from '@/lib/domain-generator'
import { generateSecurePassword, generateEmailPattern, generateEmailVariations, generateSignature } from '@/lib/utils'

interface ClientInfrastructureStepProps {
  data: ClientInfrastructureForm
  onChange: (data: Partial<ClientInfrastructureForm>) => void
  isLoading?: boolean
  tamSize?: string
  clientWebsite?: string
  clientName?: string
  client?: Client | null
}

export function ClientInfrastructureStep({ data, onChange, isLoading, tamSize, clientWebsite, clientName, client }: ClientInfrastructureStepProps) {
  const [generatedDomains, setGeneratedDomains] = useState<GeneratedDomain[]>([])
  const [isDomainsOpen, setIsDomainsOpen] = useState(false) // Collapsed by default
  const [purchasedDomains, setPurchasedDomains] = useState<Set<string>>(new Set())
  const [availabilityStatus, setAvailabilityStatus] = useState<Record<string, { available: boolean; premium?: boolean; pricing?: any }>>({})
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const [checkingDomains, setCheckingDomains] = useState<Set<string>>(new Set())
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const selectAllCheckboxRef = useRef<HTMLButtonElement>(null)
  const [purchaseDomain, setPurchaseDomain] = useState<{
    domain: string
    available?: boolean
    premium?: boolean
    pricing?: any
    clientName?: string
    clientId?: string
  } | null>(null)
  
  // Extract root domain from client website
  const rootDomain = clientWebsite ? extractRootDomain(clientWebsite) : ''
  
  // Load purchased domains from database on mount
  useEffect(() => {
    const loadPurchasedDomains = async () => {
      try {
        // Load all domains with purchase_date
        const { data: domains } = await supabase
          .from('domains')
          .select('domain, purchase_date')
          .not('purchase_date', 'is', null)
        
        if (domains) {
          const purchased = new Set(domains.map(d => d.domain.toLowerCase()))
          setPurchasedDomains(purchased)
          
          // If trycruva.com is purchased, confirm it's tracked
          if (purchased.has('trycruva.com')) {
            console.log('✅ trycruva.com is already tracked as purchased')
          }
        }
        
        // Also check if trycruva.com exists but without purchase_date, and mark it
        // (This handles the case where it was purchased but not yet synced)
        const { data: trycruvaCheck } = await supabase
          .from('domains')
          .select('domain, purchase_date')
          .eq('domain', 'trycruva.com')
          .single()
        
        if (trycruvaCheck && !trycruvaCheck.purchase_date) {
          // Domain exists but not marked as purchased - mark it now
          const { error: updateError } = await supabase
            .from('domains')
            .update({
              purchase_date: new Date().toISOString().split('T')[0],
              domain_registrar: 'Spaceship',
              status: 'Active'
            })
            .eq('domain', 'trycruva.com')
          
          if (!updateError) {
            setPurchasedDomains(prev => new Set([...prev, 'trycruva.com']))
            console.log('✅ trycruva.com marked as purchased')
          }
        }
      } catch (error) {
        console.error('Error loading purchased domains:', error)
      }
    }
    
    loadPurchasedDomains()
  }, [])
  // TAM-based calculation
  const calculateInfrastructureFromTAM = (tamSize: string) => {
    const tamMapping: { [key: string]: number } = {
      'Up to 1k leads': 1000,
      'Up to 5k leads': 5000,
      'Up to 10k leads': 10000,
      'Up to 20k leads': 20000,
      'Up to 25k leads': 25000,
      'Up to 50k leads': 50000,
      'Up to 100k leads': 100000,
      'Up to 250k leads': 250000,
      'Up to 500k leads': 500000,
      'Custom': 0
    }
    
    const monthlyLeads = tamMapping[tamSize] || 0
    if (monthlyLeads === 0) return { domains: 0, mailboxes: 0, primaryDomains: 0, backupDomains: 0, activeMailboxes: 0, reserveMailboxes: 0 }
    
    // Calculation:
    // Step 1: Total emails needed = leads × 2 touches per month
    const touchesPerMonth = 2
    const totalEmailsNeeded = monthlyLeads * touchesPerMonth
    
    // Step 2: Active mailbox capacity (FULL capacity, no reduction)
    // 1 mailbox = 25 emails/day × 5 days/week × 4 weeks/month = 500 emails/month (full capacity)
    const emailsPerMailboxPerMonth = 25 * 5 * 4 // 500 emails/month per active mailbox
    
    // Step 3: Calculate ACTIVE mailboxes needed (these will be sending)
    const activeMailboxes = Math.ceil(totalEmailsNeeded / emailsPerMailboxPerMonth)
    
    // Step 4: Active domains: 3 mailboxes per domain
    const activeDomains = Math.ceil(activeMailboxes / 3)
    
    // Step 5: Reserve infrastructure (50% = separate idle backup)
    // These domains and mailboxes are NOT sending, just ready as backup if active ones get burned
    const reserveDomainsRatio = 0.5 // 50% reserve
    const reserveDomains = Math.ceil(activeDomains * reserveDomainsRatio)
    const reserveMailboxes = reserveDomains * 3
    
    // Step 6: Total infrastructure
    const totalRequiredDomains = activeDomains + reserveDomains
    const totalRequiredMailboxes = activeMailboxes + reserveMailboxes
    
    return { 
      domains: totalRequiredDomains, 
      mailboxes: totalRequiredMailboxes, 
      primaryDomains: activeDomains, 
      backupDomains: reserveDomains,
      activeMailboxes: activeMailboxes,
      reserveMailboxes: reserveMailboxes
    }
  }

  const tamBasedInfrastructure = calculateInfrastructureFromTAM(tamSize || '')
  // Use calculated values for display, fall back to array lengths if no TAM size is set
  const calculatedDomainCount = tamSize && tamBasedInfrastructure.domains > 0 
    ? tamBasedInfrastructure.domains 
    : (data.domains_included ? data.domains_included.length : 0)
  // Mailbox count: use calculated value if TAM size is set, otherwise use number of mailbox names entered
  const calculatedMailboxCount = tamSize && tamBasedInfrastructure.mailboxes > 0
    ? tamBasedInfrastructure.mailboxes
    : (data.mailboxes_included ? data.mailboxes_included.length : 0)
  const primaryDomains = tamBasedInfrastructure.primaryDomains || 0
  const backupDomains = tamBasedInfrastructure.backupDomains || 0
  const activeMailboxes = tamBasedInfrastructure.activeMailboxes || 0
  const reserveMailboxes = tamBasedInfrastructure.reserveMailboxes || 0

  // Auto-generate candidate domains when client website changes
  // Generate only what's needed + 25% buffer for selection options
  useEffect(() => {
    if (rootDomain) {
      try {
        // Generate only what's needed + 25% buffer for selection options
        const targetDomainCount = calculatedDomainCount || 50 // Default to 50 if no TAM
        const bufferMultiplier = 1.25 // 25% extra for selection options
        const generateCount = Math.max(Math.ceil(targetDomainCount * bufferMultiplier), 50) // Min 50
        
        const candidatePool = generateDomainsForCount(
          rootDomain,
          generateCount,
          true // includeTier3
        )
        
        setGeneratedDomains(candidatePool)
        
        // Automatically check if any generated domains already exist in the database
        // Also check for ALL purchased domains matching root domain (even if not in generated list)
        // This ensures all purchased domains appear in the UI
        const checkExistingDomains = async () => {
          if (!rootDomain) return
          
          try {
            const candidateDomainNames = candidatePool.map(d => d.domain.toLowerCase())
            const candidateDomainSet = new Set(candidateDomainNames)
            
            // First, check which of the generated domains already exist in the database
            const { data: existingInGenerated, error: generatedError } = await supabase
              .from('domains')
              .select('domain, purchase_date')
              .in('domain', candidateDomainNames)
            
            if (generatedError) {
              console.error('Error checking generated domains:', generatedError)
              return
            }
            
            // Second, check for ALL purchased domains matching root domain (including those not in generated list)
            const { data: allPurchasedDomains, error: allError } = await supabase
              .from('domains')
              .select('domain, purchase_date')
              .not('purchase_date', 'is', null)
              .ilike('domain', `%${rootDomain}%.com`)
            
            if (allError) {
              console.error('Error checking all purchased domains:', allError)
              return
            }
            
            // Find purchased domains NOT in the generated list
            const missingPurchasedDomains = (allPurchasedDomains || [])
              .filter(d => !candidateDomainSet.has(d.domain.toLowerCase()))
              .map(d => ({
                domain: d.domain,
                tier: 3 as const, // Add as Tier 3 since they weren't in the generated list
                type: 'suffix' as const
              }))
            
            // Add missing purchased domains to the generated list
            if (missingPurchasedDomains.length > 0) {
              setGeneratedDomains(prev => {
                const existingSet = new Set(prev.map(d => d.domain.toLowerCase()))
                const newDomains = missingPurchasedDomains.filter(d => !existingSet.has(d.domain.toLowerCase()))
                return [...prev, ...newDomains]
              })
              console.log(`✅ Added ${missingPurchasedDomains.length} purchased domain(s) not in generated list:`, 
                missingPurchasedDomains.map(d => d.domain).join(', '))
            }
            
            // Update purchased domains set with all purchased domains
            if (allPurchasedDomains && allPurchasedDomains.length > 0) {
              setPurchasedDomains(prev => {
                const updated = new Set(prev)
                allPurchasedDomains.forEach(d => {
                  updated.add(d.domain.toLowerCase())
                })
                return updated
              })
              
              const totalPurchasedCount = allPurchasedDomains.length
              const inGeneratedCount = existingInGenerated?.length || 0
              
              console.log(`✅ Found ${totalPurchasedCount} purchased domain(s) matching root domain:`, 
                allPurchasedDomains.map(d => d.domain).join(', '))
              console.log(`   ${inGeneratedCount} in generated list, ${missingPurchasedDomains.length} added to list`)
              
              // Show toast notification with total purchased count
              if (totalPurchasedCount > 0) {
                toast.success(
                  `Found ${totalPurchasedCount} domain${totalPurchasedCount !== 1 ? 's' : ''} already in database (marked as purchased)`,
                  { duration: 3000 }
                )
              }
            }
          } catch (error) {
            console.error('Error checking existing domains:', error)
          }
        }
        
        // Check for existing domains after generation
        checkExistingDomains()
        
        // Only pre-select domains if no domains are currently selected
        // This prevents overriding user selections when domains are regenerated
        if (tamSize && tamBasedInfrastructure.domains > 0 && (!data.domains_included || data.domains_included.length === 0)) {
          const tier1Domains = candidatePool
            .filter(d => d.tier === 1)
            .slice(0, tamBasedInfrastructure.domains)
          
          const preselectedDomains = tier1Domains.map(d => d.domain)
          onChange({
            domains_included: preselectedDomains
          })
        }
      } catch (error) {
        console.error('Failed to auto-generate domains:', error)
      }
    }
  }, [tamSize, tamBasedInfrastructure.domains, rootDomain])

  // Mailbox names from form data - convert from string array to object array if needed
  const mailboxNames = (data.mailboxes_included || []).map(item => {
    if (typeof item === 'string') {
      // Check if it's a JSON string that needs parsing
      if (item.trim().startsWith('{') && item.trim().endsWith('}')) {
        try {
          const parsed = JSON.parse(item)
          // If parsing succeeds and has expected structure, use it
          if (parsed && typeof parsed === 'object') {
            return {
              firstName: parsed.firstName || '',
              lastName: parsed.lastName || '',
              title: parsed.title || '',
              // Filter out [IMAGE:...] placeholders - treat them as empty
              profilePictureUrl: (parsed.profilePictureUrl && !parsed.profilePictureUrl.startsWith('[IMAGE:')) 
                ? parsed.profilePictureUrl 
                : ''
            }
          }
        } catch (e) {
          // If JSON parsing fails, fall through to simple string handling
          console.warn('[Mailbox] Failed to parse JSON string, treating as simple name:', e)
        }
      }
      // Legacy: if it's a simple string, try to parse it or use as first name
      const parts = item.split(' ')
      return {
        firstName: parts[0] || '',
        lastName: parts.slice(1).join(' ') || '',
        title: '',
        profilePictureUrl: ''
      }
    }
    return {
      firstName: item.firstName || '',
      lastName: item.lastName || '',
      title: item.title || '',
      // Filter out [IMAGE:...] placeholders - treat them as empty
      // These placeholders are only for Excel export, not for actual image storage
      profilePictureUrl: (item.profilePictureUrl && !item.profilePictureUrl.startsWith('[IMAGE:')) 
        ? item.profilePictureUrl 
        : ''
    }
  })
  
  const [uploadingImages, setUploadingImages] = useState<Record<number, boolean>>({})
  
  const handleAddMailboxName = () => {
    onChange({
      mailboxes_included: [...mailboxNames, { firstName: '', lastName: '', title: '', profilePictureUrl: '' }]
    })
  }
  
  const handleUpdateMailboxName = (index: number, field: 'firstName' | 'lastName' | 'title', value: string) => {
    const updated = [...mailboxNames]
    updated[index] = { ...updated[index], [field]: value }
    onChange({
      mailboxes_included: updated
    })
  }
  
  const handleRemoveMailboxName = (index: number) => {
    const updated = mailboxNames.filter((_, i) => i !== index)
    onChange({
      mailboxes_included: updated
    })
  }

  const handleImageUpload = async (index: number, file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Image size must be less than 5MB')
      return
    }
    
    setUploadingImages(prev => ({ ...prev, [index]: true }))
    
    try {
      // Use Cloudinary for image hosting - simpler than Supabase storage
      const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME
      const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET
      
      if (!cloudName || !uploadPreset) {
        console.warn('[Image Upload] Cloudinary not configured, falling back to base64')
        toast.warning('Image hosting not configured. Image will be stored locally.')
        // Fallback to base64
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64String = reader.result as string
          if (base64String) {
            const updated = [...mailboxNames]
            updated[index] = { 
              ...updated[index], 
              profilePictureUrl: base64String
            }
            onChange({
              mailboxes_included: updated
            })
            toast.success('Profile picture uploaded (using local storage)')
            setUploadingImages(prev => ({ ...prev, [index]: false }))
          }
        }
        reader.onerror = () => {
          toast.error('Failed to read image file')
          setUploadingImages(prev => ({ ...prev, [index]: false }))
        }
        reader.readAsDataURL(file)
        return
      }
      
      // Upload to Cloudinary using unsigned upload preset
      const clientSlug = (clientName || 'client').replace(/[^a-z0-9]/gi, '-').toLowerCase()
      const folder = `mailbox-profiles/${clientSlug}`
      const timestamp = Date.now()
      
      const formData = new FormData()
      formData.append('file', file)
      formData.append('upload_preset', uploadPreset)
      formData.append('folder', folder)
      formData.append('public_id', `${timestamp}-${index}`)
      
      console.log('[Image Upload] Uploading to Cloudinary...')
      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: formData
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error?.message || `Upload failed: ${response.statusText}`)
      }
      
      const data = await response.json()
      const uploadedUrl = data.secure_url || data.url
      
      if (uploadedUrl) {
        const updated = [...mailboxNames]
        updated[index] = { ...updated[index], profilePictureUrl: uploadedUrl }
        console.log('[Image Upload] ✅ Successfully uploaded to Cloudinary:', {
          index,
          url: uploadedUrl,
          urlLength: uploadedUrl.length,
          mailboxName: `${updated[index].firstName} ${updated[index].lastName}`
        })
        onChange({
          mailboxes_included: updated
        })
        toast.success('Profile picture uploaded successfully!')
        setUploadingImages(prev => ({ ...prev, [index]: false }))
        return
      } else {
        throw new Error('No URL returned from Cloudinary')
      }
    } catch (error: any) {
      console.error('Error uploading image:', error)
      toast.error(`Failed to upload image: ${error.message || 'Unknown error'}`)
      setUploadingImages(prev => ({ ...prev, [index]: false }))
    }
  }

  // Export order sheet to Excel - Two sheets format
  const handleExportOrderSheet = () => {
    try {
      // Get client name
      const name = clientName || 'Outboundbuddy'
      
      // Get domains
      const domains = data.domains_included || []
      if (domains.length === 0) {
        toast.error('No domains selected. Please select domains first.')
        return
      }

      // Get mailbox names
      const mailboxNamesData = (data.mailboxes_included || []).map(item => {
        if (typeof item === 'string') {
          // Check if it's a JSON string that needs parsing
          if (item.trim().startsWith('{') && item.trim().endsWith('}')) {
            try {
              const parsed = JSON.parse(item)
              // If parsing succeeds and has expected structure, use it
              if (parsed && typeof parsed === 'object') {
                return {
                  firstName: parsed.firstName || '',
                  lastName: parsed.lastName || '',
                  title: parsed.title || '',
                  // Filter out [IMAGE:...] placeholders - treat them as empty
                  profilePictureUrl: (parsed.profilePictureUrl && !parsed.profilePictureUrl.startsWith('[IMAGE:')) 
                    ? parsed.profilePictureUrl 
                    : ''
                }
              }
            } catch (e) {
              // If JSON parsing fails, fall through to simple string handling
              console.warn('[Export] Failed to parse JSON string, treating as simple name:', e)
            }
          }
          // Legacy: if it's a simple string, try to parse it or use as first name
          const parts = item.split(' ')
          return {
            firstName: parts[0] || '',
            lastName: parts.slice(1).join(' ') || '',
            title: '',
            profilePictureUrl: ''
          }
        }
        return {
          firstName: item.firstName || '',
          lastName: item.lastName || '',
          title: item.title || '',
          // Filter out [IMAGE:...] placeholders - treat them as empty
          // These placeholders are only for Excel export when no image exists, not for actual image storage
          profilePictureUrl: (item.profilePictureUrl && !item.profilePictureUrl.startsWith('[IMAGE:')) 
            ? item.profilePictureUrl 
            : ''
        }
      })

      // Debug logging for mailbox data
      console.log('[Export] Mailbox names data:', mailboxNamesData.map((mb, idx) => {
        const url = mb.profilePictureUrl || ''
        let urlType = 'empty'
        if (url) {
          if (url.startsWith('data:')) {
            urlType = 'base64'
          } else if (url.includes('cloudinary.com') || url.includes('res.cloudinary.com')) {
            urlType = 'cloudinary'
          } else if (url.includes('supabase')) {
            urlType = 'supabase'
          } else {
            urlType = 'other'
          }
        }
        return {
          index: idx,
          name: `${mb.firstName} ${mb.lastName}`,
          hasProfilePicture: !!url,
          profilePictureUrlLength: url.length,
          profilePictureUrlType: urlType,
          profilePictureUrlPreview: url.substring(0, 150) || 'empty',
          isCloudinary: url.includes('cloudinary.com') || url.includes('res.cloudinary.com')
        }
      }))

      if (mailboxNamesData.length === 0) {
        toast.error('No mailbox names added. Please add mailbox names first.')
        return
      }

      // ============================================
      // SHEET 1: Core info
      // ============================================
      const coreInfoData: any[][] = []
      
      // Header
      coreInfoData.push(['Notes:'])
      coreInfoData.push(['Registrar', 'Provided separately.'])
      coreInfoData.push(['Sequencer', 'Provided separately.'])
      coreInfoData.push(['Warmup settings', 'Use recommended settings'])

      // ============================================
      // SHEET 2: Domains/Mailboxes
      // ============================================
      const domainsMailboxesData: any[][] = []
      
      // Header row
      domainsMailboxesData.push([
        'Domains',
        'Mailboxes',
        'Password of your inboxes',
        'User Full Name',
        'Profile Picture of your inboxes',
        'Signature',
        'Client',
        'Message Per Day (Warmups not included)',
        'Minimum time gap (min)'
      ])
      
      // Distribution logic: Divide domains equally among mailbox names
      // Each domain gets exactly 3 variations, all from the same mailbox name
      const variationsPerDomain = 3 // Each domain gets exactly 3 variations
      
      // Calculate how many domains each mailbox name should get
      const domainsPerMailbox = Math.floor(domains.length / mailboxNamesData.length)
      const remainderDomains = domains.length % mailboxNamesData.length
      
      // Create domain assignments for each mailbox
      const mailboxDomainAssignments: Array<{
        mailboxIndex: number
        domainIndices: number[]
      }> = []
      
      let domainIdx = 0
      for (let mailboxIdx = 0; mailboxIdx < mailboxNamesData.length; mailboxIdx++) {
        // Each mailbox gets base number of domains, plus 1 extra if there's remainder
        const domainsForThisMailbox = domainsPerMailbox + (mailboxIdx < remainderDomains ? 1 : 0)
        const domainIndices: number[] = []
        
        for (let i = 0; i < domainsForThisMailbox; i++) {
          domainIndices.push(domainIdx)
          domainIdx++
        }
        
        mailboxDomainAssignments.push({
          mailboxIndex: mailboxIdx,
          domainIndices
        })
      }

      // Process each mailbox's assigned domains
      for (const assignment of mailboxDomainAssignments) {
        const mailbox = mailboxNamesData[assignment.mailboxIndex]
        const fullName = `${mailbox.firstName} ${mailbox.lastName}`.trim()
        const password = generateSecurePassword(16)
        const signature = generateSignature(fullName, mailbox.title || '', name)
        const profilePictureUrl = mailbox.profilePictureUrl || ''

        // For each domain assigned to this mailbox, generate 3 variations
        for (const domainIdx of assignment.domainIndices) {
          const domain = domains[domainIdx]
          const domainVariations: Array<{
            email: string
            password: string
            fullName: string
            profilePictureUrl: string
            signature: string
            mailboxIndex: number
            variationIndex: number
          }> = []

          // Generate all 3 email variations for this domain using this mailbox name
          const emailVariations = generateEmailVariations(
            mailbox.firstName,
            mailbox.lastName,
            domain
          )

          for (let variationIdx = 0; variationIdx < variationsPerDomain; variationIdx++) {
            domainVariations.push({
              email: emailVariations[variationIdx] || `${mailbox.firstName?.toLowerCase() || 'user'}${variationIdx}@${domain}`,
              password,
              fullName,
              profilePictureUrl,
              signature,
              mailboxIndex: assignment.mailboxIndex,
              variationIndex: variationIdx
            })
          }

          // Create rows for this domain
          domainVariations.forEach((variation, idx) => {
            // Handle profile picture URL - include all valid URLs
            let profilePictureUrl = variation.profilePictureUrl || ''
            
            // Filter out any [IMAGE:...] placeholders that might have slipped through
            if (profilePictureUrl.startsWith('[IMAGE:')) {
              profilePictureUrl = ''
            }
            
            // Debug logging
            console.log(`[Export] Mailbox ${variation.mailboxIndex}, Variation ${variation.variationIndex}, Domain ${domain}:`, {
              hasProfilePictureUrl: !!profilePictureUrl,
              profilePictureUrlLength: profilePictureUrl.length,
              profilePictureUrlType: profilePictureUrl.startsWith('data:') ? 'base64' : 
                                     profilePictureUrl.includes('cloudinary.com') ? 'cloudinary' :
                                     profilePictureUrl.includes('supabase') ? 'supabase' : 
                                     profilePictureUrl ? 'other' : 'empty',
              profilePictureUrlPreview: profilePictureUrl.substring(0, 100) || 'empty'
            })
            
            // Priority order: Cloudinary URL > Supabase URL > base64 (convert to placeholder) > empty (use placeholder)
            // If it's a Cloudinary URL, keep it (highest priority - these are public and work in Excel)
            const isCloudinary = profilePictureUrl.includes('cloudinary.com') || profilePictureUrl.includes('res.cloudinary.com')
            const isSupabase = profilePictureUrl.includes('supabase.co')
            const isBase64 = profilePictureUrl.startsWith('data:')
            
            if (isCloudinary) {
              // Keep Cloudinary URLs - they're public and accessible
              console.log(`[Export] ✅ Keeping Cloudinary URL for Mailbox ${variation.mailboxIndex}, Domain ${domain}:`, profilePictureUrl.substring(0, 150))
              // Only truncate if too long for Excel (32,767 char limit)
              if (profilePictureUrl.length > 32767) {
                profilePictureUrl = profilePictureUrl.substring(0, 32760) + '...[truncated]'
              }
            }
            // If it's a Supabase URL, keep it too
            else if (isSupabase) {
              // Keep Supabase URLs - they should work if upload succeeded
              console.log(`[Export] ✅ Keeping Supabase URL for Mailbox ${variation.mailboxIndex}, Domain ${domain}:`, profilePictureUrl.substring(0, 150))
              // Only truncate if too long for Excel (32,767 char limit)
              if (profilePictureUrl.length > 32767) {
                profilePictureUrl = profilePictureUrl.substring(0, 32760) + '...[truncated]'
              }
            }
            // If it's a base64 data URL, DON'T include it (Excel can't handle long base64)
            // Just leave it empty so user knows to upload to Cloudinary
            else if (isBase64) {
              console.log(`[Export] ⚠️ Filtering out base64 URL for Mailbox ${variation.mailboxIndex}, Domain ${domain} (length: ${profilePictureUrl.length})`)
              profilePictureUrl = '' // Leave empty - base64 not useful in Excel
            }
            // If empty, leave it empty (don't create placeholders)
            // User should upload to Cloudinary to get public URLs
            else if (!profilePictureUrl) {
              console.log(`[Export] ℹ️ No profile picture URL for Mailbox ${variation.mailboxIndex}, Domain ${domain}`)
              profilePictureUrl = '' // Empty = no image uploaded yet
            }
            // If it's some other URL type, log it but keep it
            else {
              console.warn(`[Export] ⚠️ Unknown URL type for Mailbox ${variation.mailboxIndex}, Domain ${domain}:`, profilePictureUrl.substring(0, 150))
              // Keep it anyway - might be a valid URL we don't recognize
              if (profilePictureUrl.length > 32767) {
                profilePictureUrl = profilePictureUrl.substring(0, 32760) + '...[truncated]'
              }
            }
          
            // Ensure all fields are under Excel's 32767 character limit
            const truncate = (str: string, maxLength: number = 32767) => {
              if (!str) return ''
              return str.length > maxLength ? str.substring(0, maxLength - 15) + '...[truncated]' : str
            }
            
            // Final verification: Log what's being exported for Cloudinary URLs
            if (profilePictureUrl && (profilePictureUrl.includes('cloudinary.com') || profilePictureUrl.includes('res.cloudinary.com'))) {
              console.log(`[Export] ✅ FINAL: Exporting Cloudinary URL for Mailbox ${variation.mailboxIndex}, Domain ${domain}, Variation ${idx}:`, {
                url: profilePictureUrl.substring(0, 200),
                willBeExported: true,
                truncated: profilePictureUrl.length > 32767
              })
            }
            
            domainsMailboxesData.push([
              idx === 0 ? domain : '', // Show domain name only on first row
              truncate(variation.email),
              truncate(variation.password),
              truncate(variation.fullName),
              truncate(profilePictureUrl), // Include profile picture URL (Cloudinary URLs should be here)
              truncate(variation.signature),
              truncate(name),
              '25',
              '16'
            ])
          })
        }
      }

      // ============================================
      // Create workbook with both sheets
      // ============================================
      const wb = XLSX.utils.book_new()
      
      // Sheet 1: Core info
      const ws1 = XLSX.utils.aoa_to_sheet(coreInfoData)
      ws1['!cols'] = [
        { wch: 20 }, // Column A - Notes/Field names
        { wch: 30 }  // Column B - Values
      ]
      XLSX.utils.book_append_sheet(wb, ws1, 'Core info')
      
      // Sheet 2: Domains/Mailboxes
      const ws2 = XLSX.utils.aoa_to_sheet(domainsMailboxesData)
      ws2['!cols'] = [
        { wch: 25 }, // Column A - Domains
        { wch: 30 }, // Column B - Mailboxes
        { wch: 25 }, // Column C - Password
        { wch: 25 }, // Column D - User Full Name
        { wch: 40 }, // Column E - Profile Picture
        { wch: 40 }, // Column F - Signature
        { wch: 20 }, // Column G - Client
        { wch: 35 }, // Column H - Message Per Day
        { wch: 25 }  // Column I - Minimum time gap
      ]
      XLSX.utils.book_append_sheet(wb, ws2, 'Domains Mailboxes')
      
      // Generate filename
      const filename = `${name.replace(/\s+/g, '_')}_Order_Sheet_${new Date().toISOString().split('T')[0]}.xlsx`
      
      // Write file
      XLSX.writeFile(wb, filename)
      
      toast.success('Order sheet exported successfully!')
    } catch (error: any) {
      console.error('Error exporting order sheet:', error)
      toast.error(`Failed to export order sheet: ${error.message}`)
    }
  }

  // Generate additional domains if user needs more options
  const handleGenerateMoreDomains = () => {
    try {
      if (!rootDomain) {
        toast.error('Please enter a client website first')
        return
      }
      
      const currentCount = generatedDomains.length
      const additionalCount = Math.max(50, Math.ceil((calculatedDomainCount || 50) * 0.5))
      
      // Generate ALL possible variations with a large pool to ensure we get new domains
      // This works for both short and long root domains (e.g., "cruva" vs "storystream")
      const allPossibleVariations = generateDomainVariations({
        clientDomain: rootDomain,
        maxDomains: 1000, // Large pool to ensure we get all possible variations
        includePrefixes: true,
        includeTier3: true
      })
      
      // Filter to only new domains that don't already exist (case-insensitive)
      const existingSet = new Set(generatedDomains.map(d => d.domain.toLowerCase()))
      const newDomains = allPossibleVariations.filter(d => !existingSet.has(d.domain.toLowerCase()))
      
      if (newDomains.length > 0) {
        // Sort by tier (1 = best, 3 = backup) to maintain quality
        // Prefer suffixes over prefixes for better deliverability
        const sortedNewDomains = newDomains.sort((a, b) => {
          // First sort by tier (lower tier number = higher priority)
          if (a.tier !== b.tier) return a.tier - b.tier
          // Then prefer suffixes over prefixes
          if (a.type !== b.type) return a.type === 'suffix' ? -1 : 1
          return 0
        })
        
        // Take up to the requested additional count, or all available if less
        const domainsToAdd = sortedNewDomains.slice(0, Math.min(additionalCount, newDomains.length))
        setGeneratedDomains([...generatedDomains, ...domainsToAdd])
        toast.success(`Generated ${domainsToAdd.length} additional domain${domainsToAdd.length !== 1 ? 's' : ''}`)
      } else {
        // Provide helpful feedback about why no more domains are available
        const totalPossible = allPossibleVariations.length
        toast.info(
          `No additional unique domains available. ${totalPossible > 0 ? `Total possible: ${totalPossible}. All have been generated.` : 'No valid domain variations found for this root domain.'}`
        )
      }
    } catch (error) {
      console.error('Error generating more domains:', error)
      toast.error('Failed to generate additional domains. Please try again.')
    }
  }

  // Toggle domain selection
  const handleToggleDomain = (domain: string) => {
    const currentDomains = data.domains_included || []
    const isSelected = currentDomains.includes(domain)
    
    if (isSelected) {
      onChange({
        domains_included: currentDomains.filter(d => d !== domain)
      })
    } else {
      onChange({
        domains_included: [...currentDomains, domain]
      })
    }
  }

  // Check availability for generated domains
  const handleCheckAvailability = async () => {
    if (generatedDomains.length === 0) {
      toast.error('No domains to check')
      return
    }
    
    setIsCheckingAvailability(true)
    const checkingSet = new Set<string>()
    const newAvailabilityStatus: Record<string, { available: boolean; premium?: boolean; pricing?: any }> = { ...availabilityStatus }
    
    try {
      // Spaceship API rate limit: EXACTLY 30 requests per user within 30 seconds
      // We'll process domains sequentially and track requests in a sliding 30-second window
      const domainsToCheck = generatedDomains.map(d => d.domain)
      const RATE_LIMIT = 30 // requests
      const RATE_WINDOW_MS = 30000 // 30 seconds in milliseconds
      const requestTimestamps: number[] = [] // Track request timestamps in sliding window
      
      // Helper function to wait if we've hit the rate limit
      const waitForRateLimit = async () => {
        let now = Date.now()
        // Remove timestamps older than 30 seconds
        let recentRequests = requestTimestamps.filter(ts => now - ts < RATE_WINDOW_MS)
        requestTimestamps.length = 0
        requestTimestamps.push(...recentRequests)
        
        // If we've hit the limit, wait until the oldest request is 30 seconds old
        while (requestTimestamps.length >= RATE_LIMIT) {
          const oldestRequest = Math.min(...requestTimestamps)
          const waitTime = RATE_WINDOW_MS - (now - oldestRequest) + 1000 // Add 1 second buffer for safety
          
          if (waitTime > 0) {
            const waitSeconds = Math.ceil(waitTime / 1000)
            console.log(`[RATE LIMIT] ${requestTimestamps.length}/${RATE_LIMIT} requests in last 30s. Waiting ${waitSeconds}s...`)
            toast.info(`Rate limit: Waiting ${waitSeconds}s (${requestTimestamps.length}/${RATE_LIMIT} requests used)`, { duration: 3000 })
            await new Promise(resolve => setTimeout(resolve, waitTime))
            
            // Recalculate after waiting - some requests may have aged out
            now = Date.now()
            recentRequests = requestTimestamps.filter(ts => now - ts < RATE_WINDOW_MS)
            requestTimestamps.length = 0
            requestTimestamps.push(...recentRequests)
          } else {
            // If waitTime is 0 or negative, break to avoid infinite loop
            break
          }
        }
      }
      
      // Process domains one at a time to respect rate limits exactly
      for (let i = 0; i < domainsToCheck.length; i++) {
        const domain = domainsToCheck[i]
        
        // Check and wait for rate limit before each request
        await waitForRateLimit()
        
        // Mark as checking
        checkingSet.add(domain)
        setCheckingDomains(new Set(checkingSet))
        
        try {
          // Add a minimum delay between requests to ensure we don't burst
          // Spaceship API: 30 requests per 30 seconds = 1 request per second minimum
          // We'll do 1 request per 1.2 seconds to be safe (allows ~25 requests per 30s, well under limit)
          if (requestTimestamps.length > 0) {
            const now = Date.now()
            const lastRequest = requestTimestamps[requestTimestamps.length - 1]
            const timeSinceLastRequest = now - lastRequest
            const minDelay = 1200 // 1.2 seconds between requests (safer margin)
            
            if (timeSinceLastRequest < minDelay) {
              const waitTime = minDelay - timeSinceLastRequest
              console.log(`[RATE LIMIT] Ensuring ${waitTime}ms delay between requests (min ${minDelay}ms)`)
              await new Promise(resolve => setTimeout(resolve, waitTime))
            }
          }
          
          // Record request timestamp AFTER ensuring delay
          const requestTime = Date.now()
          
          // Clean up old timestamps before adding new one
          const now = Date.now()
          const recentRequests = requestTimestamps.filter(ts => now - ts < RATE_WINDOW_MS)
          requestTimestamps.length = 0
          requestTimestamps.push(...recentRequests)
          
          // Now add the new request
          requestTimestamps.push(requestTime)
          
          console.log(`[FRONTEND] Checking availability for: ${domain} (${requestTimestamps.length}/${RATE_LIMIT} in window)`)
          
          const { data: responseData, error } = await supabase.functions.invoke('check-domain-pricing', {
            body: { domainName: domain }
          })
          
          console.log(`[FRONTEND] Response for ${domain}:`, {
            hasData: !!responseData,
            hasError: !!error,
            responseData: responseData,
            error: error
          })
          
          if (error) {
            console.error(`[FRONTEND] Error checking availability for ${domain}:`, error)
            // Don't mark as "taken" on error - mark as "unknown" (null)
            newAvailabilityStatus[domain.toLowerCase()] = { 
              available: null, // null = unknown/error, not "taken"
              error: error.message || 'Error checking availability'
            }
          } else if (responseData && responseData.success) {
            console.log(`[FRONTEND] Success response for ${domain}:`, {
              available: responseData.available,
              availableType: typeof responseData.available,
              isPremium: responseData.isPremium,
              fullResponse: responseData
            })
            
            // Only mark as "taken" if explicitly not available
            // null means error/unknown, false means actually taken
            const available = responseData.available === true
            const isUnknown = responseData.available === null || responseData.available === undefined
            
            console.log(`[FRONTEND] Parsed availability for ${domain}:`, {
              available,
              isUnknown,
              finalAvailable: isUnknown ? null : available
            })
            
            newAvailabilityStatus[domain.toLowerCase()] = {
              available: isUnknown ? null : available, // null for unknown, false for taken, true for available
              premium: responseData.isPremium === true,
              pricing: responseData.pricing,
              error: responseData.error || null
            }
          } else {
            // Error response - don't mark as "taken"
            console.warn(`[FRONTEND] Failed to check availability for ${domain}:`, responseData?.error || 'Unknown error', responseData)
            newAvailabilityStatus[domain.toLowerCase()] = { 
              available: null, // null = unknown/error
              error: responseData?.error || 'Failed to check availability'
            }
          }
          
          // Update state after each request
          setAvailabilityStatus({ ...newAvailabilityStatus })
        } catch (error: any) {
          console.error(`[FRONTEND] Exception checking availability for ${domain}:`, error)
          // Don't mark as "taken" on exception - mark as "unknown"
          newAvailabilityStatus[domain.toLowerCase()] = { 
            available: null, // null = unknown/error, not "taken"
            error: error.message || 'Exception checking availability'
          }
          setAvailabilityStatus({ ...newAvailabilityStatus })
        } finally {
          // Remove from checking set
          checkingSet.delete(domain)
          setCheckingDomains(new Set(checkingSet))
        }
      }
      
      // Final update
      setAvailabilityStatus(newAvailabilityStatus)
      
      const availableCount = Object.values(newAvailabilityStatus).filter(r => r.available).length
      const totalChecked = Object.keys(newAvailabilityStatus).length
      
      toast.success(
        `Availability checked: ${availableCount} available out of ${totalChecked} checked`
      )
    } catch (error: any) {
      toast.error(`Failed to check availability: ${error.message}`)
    } finally {
      setIsCheckingAvailability(false)
      setCheckingDomains(new Set())
    }
  }

  // Use available domains (up to target count)
  const handleUseAvailableDomains = () => {
    // Only use domains that are explicitly available (true), not errors (null) or taken (false)
    const available = generatedDomains
      .filter(d => availabilityStatus[d.domain.toLowerCase()]?.available === true)
    
    if (available.length === 0) {
      const errorCount = generatedDomains.filter(d => availabilityStatus[d.domain.toLowerCase()]?.available === null).length
      if (errorCount > 0) {
        toast.error(`No available domains found. ${errorCount} domain(s) had errors checking availability. Please try checking again.`)
      } else {
        toast.error('No available domains found. Please check availability first.')
      }
      return
    }
    
    // Prioritize by tier, then by generation order
    const sortedAvailable = available.sort((a, b) => {
      if (a.tier !== b.tier) return a.tier - b.tier // Tier 1 first
      return 0 // Keep generation order within same tier
    })
    
    // Use target count if specified, otherwise use all available
    const targetCount = tamSize && tamBasedInfrastructure.domains > 0 
      ? tamBasedInfrastructure.domains 
      : available.length
    
    const selectedDomains = sortedAvailable
      .slice(0, targetCount)
      .map(d => d.domain)
    
    onChange({
      ...data,
      domains_included: selectedDomains
    })
    toast.success(
      `Selected ${selectedDomains.length} available domain${selectedDomains.length !== 1 ? 's' : ''}${targetCount < available.length ? ` (${available.length - targetCount} more available)` : ''}`
    )
  }

  // Select all purchased domains from the generated list
  const handleSelectAllPurchased = () => {
    const purchasedDomainList = generatedDomains
      .filter(d => purchasedDomains.has(d.domain.toLowerCase()))
      .map(d => d.domain)
    
    if (purchasedDomainList.length === 0) {
      toast.info('No purchased domains found in the list')
      return
    }
    
    onChange({
      ...data,
      domains_included: purchasedDomainList
    })
    
    toast.success(
      `Selected ${purchasedDomainList.length} purchased domain${purchasedDomainList.length !== 1 ? 's' : ''}`
    )
  }

  // Toggle all domains selection
  const handleToggleAllDomains = () => {
    const selectedDomains = data.domains_included || []
    const allDomainNames = generatedDomains.map(d => d.domain)
    const allSelected = allDomainNames.every(domain => selectedDomains.includes(domain))
    
    if (allSelected) {
      // Uncheck all
      onChange({
        ...data,
        domains_included: []
      })
      toast.success('Unchecked all domains')
    } else {
      // Check all
      onChange({
        ...data,
        domains_included: allDomainNames
      })
      toast.success(`Selected all ${allDomainNames.length} domain${allDomainNames.length !== 1 ? 's' : ''}`)
    }
  }

  // Update indeterminate state for select all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const selectedCount = data.domains_included?.length || 0
      const totalCount = generatedDomains.length
      selectAllCheckboxRef.current.indeterminate = selectedCount > 0 && selectedCount < totalCount
    }
  }, [data.domains_included, generatedDomains.length])

  return (
    <div className="space-y-6">
      {/* Domains */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Domains</span>
            {rootDomain && (
              <Badge variant="outline" className="text-xs">
                Based on: {rootDomain}.com
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Domain Count</Label>
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {calculatedDomainCount}
              </div>
              <div className="text-sm text-muted-foreground">
                {tamSize ? `${tamSize} = ${calculatedDomainCount} domains` : 'No TAM size selected'}
              </div>
              {tamSize && primaryDomains > 0 && (
                <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                  {primaryDomains} active domains + {backupDomains} reserve domains (idle backup)
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Automatically calculated based on TAM size, lead volume requirements, and backup domains for deliverability
            </p>
          </div>


          {/* Generated Domains List - Collapsible Table */}
          {generatedDomains.length > 0 && (
            <Collapsible open={isDomainsOpen} onOpenChange={setIsDomainsOpen} className="pt-4 border-t">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="p-0 h-auto font-medium hover:bg-transparent flex items-start gap-2"
                  >
                    {isDomainsOpen ? (
                      <ChevronDown className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="text-left">
                      <Label className="text-sm font-medium cursor-pointer block">
                        Candidate Domains ({generatedDomains.length} generated)
                      </Label>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {data.domains_included && data.domains_included.length > 0 ? (
                          <span>{data.domains_included.length} selected</span>
                        ) : (
                          <span>Select domains from the list below</span>
                        )}
                        {(() => {
                          // Count ALL purchased domains matching root domain, not just those in generatedDomains
                          const purchasedCount = rootDomain 
                            ? Array.from(purchasedDomains).filter(d => d.includes(rootDomain.toLowerCase())).length
                            : generatedDomains.filter(d => purchasedDomains.has(d.domain.toLowerCase())).length
                          return purchasedCount > 0 && (
                            <span className="ml-2 text-blue-600 font-medium">
                              • {purchasedCount} purchased
                            </span>
                          )
                        })()}
                        {tamSize && tamBasedInfrastructure.domains > 0 && (
                          <span className="ml-2">
                            • Target: {tamBasedInfrastructure.domains} domains needed
                          </span>
                        )}
                      </div>
                    </div>
                  </Button>
                </CollapsibleTrigger>
                <div className="flex gap-2 flex-wrap flex-shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCheckAvailability}
                    disabled={isCheckingAvailability || isLoading || generatedDomains.length === 0}
                  >
                    {isCheckingAvailability ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Check Availability
                      </>
                    )}
                  </Button>
                  {(() => {
                    // Count purchased domains in the generated list
                    const purchasedInList = generatedDomains.filter(d => purchasedDomains.has(d.domain.toLowerCase())).length
                    return purchasedInList > 0 && (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={handleSelectAllPurchased}
                        disabled={isLoading || purchasedInList === 0}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Select All Purchased ({purchasedInList})
                      </Button>
                    )
                  })()}
                  {Object.keys(availabilityStatus).length > 0 && Object.values(availabilityStatus).some(r => r.available) && (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      onClick={handleUseAvailableDomains}
                      disabled={isLoading}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Use Available
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateMoreDomains}
                    disabled={!rootDomain || isLoading}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Generate More
                  </Button>
                </div>
              </div>
              
              <CollapsibleContent>
                <div className="mt-3 border rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto overflow-x-auto">
                    <Table className="min-w-full">
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead className="w-[50px] min-w-[50px]">
                            <Checkbox
                              ref={selectAllCheckboxRef}
                              checked={
                                generatedDomains.length > 0 &&
                                generatedDomains.every(d => data.domains_included?.includes(d.domain))
                              }
                              onCheckedChange={handleToggleAllDomains}
                              disabled={isLoading || generatedDomains.length === 0}
                            />
                          </TableHead>
                          <TableHead className="w-[280px] min-w-[280px]">Domain</TableHead>
                          <TableHead className="w-[120px] min-w-[120px] text-center">Status</TableHead>
                          <TableHead className="w-[120px] min-w-[120px] text-center">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {generatedDomains.map((domain, index) => {
                          const isSelected = data.domains_included?.includes(domain.domain)
                          const isPurchased = purchasedDomains.has(domain.domain.toLowerCase())
                          const domainStatus = availabilityStatus[domain.domain.toLowerCase()]
                          const hasBeenChecked = domainStatus !== undefined
                          const isAvailable = domainStatus?.available === true
                          const isPremium = domainStatus?.premium === true
                          const isChecking = checkingDomains.has(domain.domain)
                          
                          return (
                            <TableRow
                              key={`${domain.domain}-${index}`}
                              className={
                                isPurchased
                                  ? 'bg-blue-50/50 hover:bg-blue-50'
                                  : hasBeenChecked && isAvailable
                                  ? 'bg-green-50/50 hover:bg-green-50'
                                  : hasBeenChecked && !isAvailable
                                  ? 'bg-red-50/50 hover:bg-red-50'
                                  : ''
                              }
                            >
                              <TableCell className="w-12">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleToggleDomain(domain.domain)}
                                  disabled={hasBeenChecked && !isAvailable}
                                />
                              </TableCell>
                              <TableCell className="font-mono text-sm font-medium min-w-[280px]">
                                <div className="flex items-center gap-2">
                                  <span className="whitespace-nowrap">{domain.domain}</span>
                                  {isPurchased && (
                                    <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300 whitespace-nowrap">
                                      Purchased
                                    </Badge>
                                  )}
                                  {isPremium && (
                                    <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300 whitespace-nowrap">
                                      Premium
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center min-w-[120px]">
                                <div className="min-h-[20px] flex items-center justify-center">
                                  {isPurchased && (
                                    <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300 whitespace-nowrap">
                                      Purchased
                                    </Badge>
                                  )}
                                  {!isPurchased && isChecking && (
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0" />
                                  )}
                                  {!isPurchased && !isChecking && hasBeenChecked && isAvailable === true && (
                                    <Badge variant="default" className="text-xs whitespace-nowrap bg-green-600">
                                      Available
                                    </Badge>
                                  )}
                                  {!isPurchased && !isChecking && hasBeenChecked && isAvailable === false && (
                                    <div className="flex items-center justify-center gap-1">
                                      <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                                      <span className="text-xs text-red-600 whitespace-nowrap">Taken</span>
                                    </div>
                                  )}
                                  {!isPurchased && !isChecking && hasBeenChecked && isAvailable === null && (
                                    <div className="flex items-center justify-center gap-1" title={domainStatus?.error || 'Error checking availability'}>
                                      <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                      <span className="text-xs text-amber-600 whitespace-nowrap">Error</span>
                                    </div>
                                  )}
                                  {!isPurchased && !isChecking && !hasBeenChecked && (
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">—</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                {!isPurchased && hasBeenChecked && isAvailable ? (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => {
                                      setPurchaseDomain({
                                        domain: domain.domain,
                                        available: isAvailable,
                                        premium: isPremium,
                                        pricing: domainStatus?.pricing,
                                        clientName: client?.name || clientName,
                                        clientId: client?.id || undefined,
                                      })
                                      setShowPurchaseModal(true)
                                    }}
                                    className="gap-1 bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    <ShoppingCart className="h-3 w-3" />
                                    Purchase
                                  </Button>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                
                {Object.keys(availabilityStatus).length > 0 && (
                  <div className="text-xs text-muted-foreground mt-2 space-y-1">
                    <p>
                      <strong>{Object.values(availabilityStatus).filter(r => r.available === true).length}</strong> domains available, 
                      <strong> {Object.values(availabilityStatus).filter(r => r.available === false).length}</strong> taken
                      {Object.values(availabilityStatus).filter(r => r.available === null).length > 0 && (
                        <span className="text-amber-600 ml-1">
                          • <strong>{Object.values(availabilityStatus).filter(r => r.available === null).length}</strong> error checking
                        </span>
                      )}
                    </p>
                    {(() => {
                      // Count ALL purchased domains matching root domain, not just those in generatedDomains
                      const purchasedCount = rootDomain 
                        ? Array.from(purchasedDomains).filter(d => d.includes(rootDomain.toLowerCase())).length
                        : generatedDomains.filter(d => purchasedDomains.has(d.domain.toLowerCase())).length
                      return purchasedCount > 0 && (
                        <p className="text-blue-600 font-medium">
                          <strong>{purchasedCount}</strong> domain{purchasedCount !== 1 ? 's' : ''} purchased
                        </p>
                      )
                    })()}
                    {tamSize && tamBasedInfrastructure.domains > 0 && (
                      <p>
                        Target: <strong>{tamBasedInfrastructure.domains}</strong> domains needed
                        {Object.values(availabilityStatus).filter(r => r.available).length >= tamBasedInfrastructure.domains ? (
                          <span className="text-green-600 ml-1">✓ Enough available</span>
                        ) : (
                          <span className="text-amber-600 ml-1">
                            ⚠ Need {tamBasedInfrastructure.domains - Object.values(availabilityStatus).filter(r => r.available).length} more
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-muted-foreground/80">
                      {generatedDomains.length - Object.keys(availabilityStatus).length} domains not yet checked
                    </p>
                  </div>
                )}
                {Object.keys(availabilityStatus).length === 0 && generatedDomains.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-2 space-y-1">
                    <p>
                      Click "Check Availability" to verify availability for all {generatedDomains.length} candidate domains
                    </p>
                    {(() => {
                      // Count ALL purchased domains matching root domain, not just those in generatedDomains
                      const purchasedCount = rootDomain 
                        ? Array.from(purchasedDomains).filter(d => d.includes(rootDomain.toLowerCase())).length
                        : generatedDomains.filter(d => purchasedDomains.has(d.domain.toLowerCase())).length
                      return purchasedCount > 0 && (
                        <p className="text-blue-600 font-medium">
                          <strong>{purchasedCount}</strong> domain{purchasedCount !== 1 ? 's' : ''} already purchased
                        </p>
                      )
                    })()}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}

        </CardContent>
      </Card>

      {/* Mailboxes */}
      <Card>
        <CardHeader>
          <CardTitle>Mailboxes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Mailbox Count</Label>
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {calculatedMailboxCount}
              </div>
              <div className="text-sm text-muted-foreground">
                {tamSize ? `${tamSize} = ${calculatedMailboxCount} mailboxes` : 'No TAM size selected'}
              </div>
            </div>
            {tamSize && activeMailboxes > 0 && (
              <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                {activeMailboxes} active mailboxes (sending) + {reserveMailboxes} reserve mailboxes (idle backup)
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              3 mailboxes per domain. Reserve infrastructure (50%) are idle backups, ready if active ones get burned.
            </p>
          </div>

          {/* Math Breakdown */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm font-medium text-blue-900 mb-2">Capacity Calculation</div>
            <div className="text-sm text-blue-800 space-y-1">
              {tamSize && tamBasedInfrastructure.mailboxes > 0 ? (
                <>
                  <div>{tamSize.replace('Up to ', '')} × 2 touches/month = {parseInt(tamSize.match(/\d+/)?.[0] || '0') * 2}k emails/month</div>
                  <div>25 emails/day × 5 days/week × 4 weeks/month = 500 emails/month per mailbox</div>
                  <div><strong>{activeMailboxes} active mailboxes</strong> needed (sending at full capacity)</div>
                  <div><strong>{primaryDomains} active domains</strong> ({activeMailboxes} mailboxes ÷ 3 per domain)</div>
                  <div className="pt-2 mt-2 border-t border-blue-300">
                    <div className="font-medium mb-1">50% Reserve (Idle Backup):</div>
                    <div>+ <strong>{reserveMailboxes} reserve mailboxes</strong> (NOT sending, just ready as backup)</div>
                    <div>+ <strong>{backupDomains} reserve domains</strong> (idle, ready if active ones get burned)</div>
                  </div>
                  <div className="pt-2 mt-2 border-t border-blue-300 font-medium">
                    <strong>Total: {calculatedMailboxCount} mailboxes + {calculatedDomainCount} domains</strong>
                  </div>
                </>
              ) : (
                <>
                  <div>25 emails/day × 5 days/week × 4 weeks/month = 500 emails/month per mailbox</div>
                  <div><strong>{calculatedMailboxCount} mailboxes</strong> configured</div>
                </>
              )}
            </div>
          </div>

          {/* Mailbox Names */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Mailbox Names</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddMailboxName}
                disabled={isLoading}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Name
              </Button>
            </div>
            {mailboxNames.length > 0 ? (
              <div className="space-y-3">
                {mailboxNames.map((mailbox, index) => (
                  <div key={index} className="p-3 border rounded-lg space-y-2 bg-muted/20">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-muted-foreground">Mailbox {index + 1}</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMailboxName(index)}
                        disabled={isLoading}
                      >
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">First Name</Label>
                        <Input
                          placeholder="First name"
                          value={mailbox.firstName || ''}
                          onChange={(e) => handleUpdateMailboxName(index, 'firstName', e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Last Name</Label>
                        <Input
                          placeholder="Last name"
                          value={mailbox.lastName || ''}
                          onChange={(e) => handleUpdateMailboxName(index, 'lastName', e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Title</Label>
                        <Input
                          placeholder="Title (optional)"
                          value={mailbox.title || ''}
                          onChange={(e) => handleUpdateMailboxName(index, 'title', e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    {/* Profile Picture Upload */}
                    <div className="space-y-1 pt-2 border-t">
                      <Label className="text-xs">Profile Picture</Label>
                      <div className="flex items-center gap-3">
                        {mailbox.profilePictureUrl ? (
                          <div className="flex items-center gap-3 flex-1">
                            <Avatar className="h-12 w-12">
                              <AvatarImage src={mailbox.profilePictureUrl} alt={`${mailbox.firstName} ${mailbox.lastName}`} />
                              <AvatarFallback>
                                {mailbox.firstName?.[0]}{mailbox.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="text-xs text-muted-foreground">Picture uploaded</p>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = [...mailboxNames]
                                  updated[index] = { ...updated[index], profilePictureUrl: '' }
                                  onChange({ mailboxes_included: updated })
                                }}
                                className="text-xs h-6 mt-1"
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-1">
                            <label htmlFor={`profile-picture-${index}`} className="cursor-pointer">
                              <input
                                id={`profile-picture-${index}`}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0]
                                  if (file) {
                                    handleImageUpload(index, file)
                                  }
                                }}
                                disabled={isLoading || uploadingImages[index]}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                disabled={isLoading || uploadingImages[index]}
                                asChild
                              >
                                <span>
                                  {uploadingImages[index] ? (
                                    <>
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="h-4 w-4" />
                                      Upload Photo
                                    </>
                                  )}
                                </span>
                              </Button>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg bg-muted/20">
                No mailbox names added. Click "Add Name" to create one.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Enter first name, last name, optional title, and profile picture for each mailbox
            </p>
            
            {/* Export Order Sheet Button */}
            <div className="flex justify-end pt-4 border-t mt-4">
              <Button
                type="button"
                variant="default"
                onClick={handleExportOrderSheet}
                disabled={isLoading || !data.domains_included || data.domains_included.length === 0}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Export Order Sheet
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LinkedIn Accounts */}
      <Card>
        <CardHeader>
          <CardTitle>LinkedIn Accounts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">LinkedIn Accounts Count</Label>
            <Input
              type="number"
              min="0"
              placeholder="Enter number of LinkedIn accounts..."
              value={data.linkedin_accounts_count || ''}
              onChange={(e) => onChange({ linkedin_accounts_count: parseInt(e.target.value) || 0 })}
              className="w-32"
            />
            <p className="text-xs text-muted-foreground">
              Number of LinkedIn accounts that will be used for outreach campaigns
            </p>
          </div>

          {/* LinkedIn Account Details */}
          {data.linkedin_accounts_count && data.linkedin_accounts_count > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">LinkedIn Account Details</Label>
              <div className="space-y-3">
                {Array.from({ length: data.linkedin_accounts_count }, (_, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-muted/20">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Account Name</Label>
                        <Input
                          placeholder={`LinkedIn Account ${index + 1}`}
                          value={data.linkedin_account_names?.[index] || ''}
                          onChange={(e) => {
                            const names = data.linkedin_account_names || []
                            names[index] = e.target.value
                            onChange({ linkedin_account_names: names })
                          }}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Profile Link</Label>
                        <Input
                          placeholder="https://linkedin.com/in/username"
                          value={data.linkedin_account_links?.[index] || ''}
                          onChange={(e) => {
                            const links = data.linkedin_account_links || []
                            links[index] = e.target.value
                            onChange({ linkedin_account_links: links })
                          }}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">HeyReach Connected</Label>
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={data.linkedin_heyreach_connected?.[index] || false}
                            onCheckedChange={(checked) => {
                              const connected = data.linkedin_heyreach_connected || []
                              connected[index] = checked
                              onChange({ linkedin_heyreach_connected: connected })
                            }}
                          />
                          <span className="text-xs text-muted-foreground">
                            {data.linkedin_heyreach_connected?.[index] ? 'Connected' : 'Not Connected'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Configure each LinkedIn account with name, profile link, and HeyReach connection status
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cold Calling */}
      <Card>
        <CardHeader>
          <CardTitle>Cold Calling</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-3">
            <Switch
              checked={data.cold_calling_included || false}
              onCheckedChange={(checked) => onChange({ cold_calling_included: checked })}
              disabled={isLoading}
            />
            <div>
              <Label className="text-sm font-medium">Include Cold Calling</Label>
              <p className="text-xs text-muted-foreground">
                Enable cold calling as part of the outreach strategy
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Domain Purchase Modal */}
      {purchaseDomain && (
        <DomainPurchaseModal
          open={showPurchaseModal}
          onOpenChange={(open) => {
            setShowPurchaseModal(open)
            if (!open) {
              setPurchaseDomain(null)
            }
          }}
          domain={purchaseDomain}
          clientId={purchaseDomain.clientId || client?.id || undefined}
          clientName={purchaseDomain.clientName || client?.name || clientName}
          onPurchaseComplete={async () => {
            const purchasedDomainName = purchaseDomain.domain
            
            // Add purchased domain to client's infrastructure domains_included array
            const currentDomains = data?.domains_included || []
            if (!currentDomains.includes(purchasedDomainName)) {
              onChange({
                domains_included: [...currentDomains, purchasedDomainName]
              })
              console.log(`✅ Added ${purchasedDomainName} to client infrastructure domains_included`)
            }
            
            // Refresh purchased domains list and verify the domain was added to the database
            const loadPurchasedDomains = async () => {
              try {
                // Wait a moment for the database to update
                await new Promise(resolve => setTimeout(resolve, 1000))
                
                const { data: domains, error } = await supabase
                  .from('domains')
                  .select('domain, purchase_date, client_name')
                  .not('purchase_date', 'is', null)
                
                if (error) {
                  console.error('Error loading purchased domains:', error)
                  return
                }
                
                if (domains) {
                  const purchased = new Set(domains.map(d => d.domain.toLowerCase()))
                  setPurchasedDomains(purchased)
                  
                  // Verify the purchased domain exists in the database
                  const purchasedDomain = domains.find(d => d.domain.toLowerCase() === purchasedDomainName.toLowerCase())
                  if (purchasedDomain) {
                    console.log(`✅ Domain ${purchasedDomainName} purchased with client_name:`, purchasedDomain.client_name || 'not set')
                    console.log(`✅ Domain ${purchasedDomainName} purchase_date:`, purchasedDomain.purchase_date)
                  } else {
                    console.warn(`⚠️ Domain ${purchasedDomainName} not found in database after purchase. It may still be processing.`)
                    // Try again after a longer delay
                    setTimeout(async () => {
                      const { data: retryDomains } = await supabase
                        .from('domains')
                        .select('domain, purchase_date, client_name')
                        .eq('domain', purchasedDomainName.toLowerCase())
                        .single()
                      
                      if (retryDomains) {
                        const updated = new Set(purchasedDomains)
                        updated.add(retryDomains.domain.toLowerCase())
                        setPurchasedDomains(updated)
                        console.log(`✅ Domain ${purchasedDomainName} found on retry`)
                      }
                    }, 3000)
                  }
                }
              } catch (error) {
                console.error('Error loading purchased domains:', error)
              }
            }
            await loadPurchasedDomains()
            
            toast.success(`Domain ${purchasedDomainName} purchased and added to infrastructure!`)
            setShowPurchaseModal(false)
            setPurchaseDomain(null)
          }}
        />
      )}
    </div>
  )
}